import {parseNumber} from '@benchling/js-utils';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import FormInput, {InputGroupPosition} from 'ui-library/bs/FormInput';
import {ElementKeyboardEvent, ElementMouseEvent, InputChangeEvent, NumericInputChangeEvent} from 'util/Types';

export interface NumericInputProps {
  className?: string;
  focusOnMount?: boolean;
  id?: string;
  isDisabled: boolean;
  min?: number;
  max?: number;
  onChange: (e: NumericInputChangeEvent) => void;
  onClick?: (e: ElementMouseEvent) => void;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  placeholder?: string;
  required: boolean;
  value?: string | number | null;
  validateInteger: boolean;
  dataTestAttrs?: DataTestAttrs;
  inputGroupPosition?: InputGroupPosition;
  selectAllOnClick?: boolean;
  useNumberInputType?: boolean;
}

interface State {
  stringValue?: string;
}

/*
 *  A component that simulates an HTML input with `type: number`. This component accepts `number` values as
 *  props, rather than `string` values, and handles the number -> conversion for display in the `input`.
 *  It also omits the increment/decrement button on `type: number` inputs, and allows null numeric values.
 *
 *  `this.props.value` is the value as a number, while `this.state.value` is the value as a string.
 *
 *  Calls the onChange prop with a modified event, with the numeric value in `e.target.numericValue` and
 *  whether or not the input is valid in `e.target.isValid`.
 */

class NumericInput extends React.Component<NumericInputProps, State> {
  static propTypes = {
    className: PropTypes.string,
    focusOnMount: PropTypes.bool,
    id: PropTypes.string,
    isDisabled: PropTypes.bool.isRequired,
    min: PropTypes.number,
    max: PropTypes.number,
    // Called with the HTML event, but with an additional `e.target.numericValue`, which is the numeric
    // representation of `e.target.value` if it's a valid number, otherwise null. `e.target.value` is left
    // unmodified.
    //
    // Remember to access `e.target.numericValue` in parent components for the number!
    onChange: PropTypes.func.isRequired,
    onClick: PropTypes.func,
    onKeyDown: PropTypes.func,
    placeholder: PropTypes.string,
    required: PropTypes.bool.isRequired,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    validateInteger: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    isDisabled: false,
    required: false,
    validateInteger: false,
    dataTestAttrs: {component: 'NumericInput', element: 'root'},
  };

  constructor(props: NumericInputProps) {
    super(props);
    this.state = {
      stringValue: props.value?.toString() ?? '',
    };
  }

  componentDidUpdate(prevProps: NumericInputProps): void {
    if (prevProps.value !== this.props.value) {
      this.setState({stringValue: this.props.value?.toString() ?? ''});
    }
  }

  private handleChange = (e: InputChangeEvent): void => {
    const isValid = !this.isInvalid(e.target.value);
    const numericValue = isValid ? parseNumber(e.target.value) : null;
    this.props.onChange({target: {value: e.target.value, isValid, numericValue}});
    this.setState({stringValue: e.target.value});
  };

  private isInvalid = (stringValue: string | null | undefined): boolean => {
    if (!stringValue) {
      return this.props.required;
    }

    const numericValue = parseNumber(stringValue);
    if (numericValue == null) {
      return true;
    }
    if (this.props.validateInteger && !Number.isInteger(numericValue)) {
      return true;
    }
    if (this.props.min != null && numericValue < this.props.min) {
      return true;
    }
    if (this.props.max != null && numericValue > this.props.max) {
      return true;
    }
    return false;
  };

  render(): JSX.Element {
    return (
      <FormInput
        className={classNames(this.props.className, {
          'fieldInput-input--error': this.isInvalid(this.state.stringValue),
        })}
        id={this.props.id}
        disabled={this.props.isDisabled}
        focusOnMount={this.props.focusOnMount}
        onChange={this.handleChange}
        onClick={this.props.onClick}
        onKeyDown={this.props.onKeyDown}
        placeholder={this.props.placeholder}
        value={this.state.stringValue}
        inputGroupPosition={this.props.inputGroupPosition}
        selectAllOnClick={this.props.selectAllOnClick}
        type={this.props.useNumberInputType ? 'number' : undefined}
        {...getTestProps(this.props.dataTestAttrs)}
      />
    );
  }
}

export default NumericInput;
